import React, { useState, useEffect } from "react";
import Nav from "./Nav";

function Monitor() {
    const [monitoringEnabled, setMonitoringEnabled] = useState(true);
    const [rules, setRules] = useState([]);
    const [showCreateRule, setShowCreateRule] = useState(false);
    const [liveFeed, setLiveFeed] = useState([]);
    const [userFeed, setUserFeed] = useState([]);

    // Helper function to get JWT token
    const getAuthToken = () => {
        return document.cookie
            .split('; ')
            .find(row => row.startsWith('token='))
            ?.split('=')[1];
    };

    // Helper function to get user ID from JWT token
    const getUserIdFromToken = () => {
        const token = getAuthToken();
        if (!token) return null;
        try {
            const payload = token.split('.')[1];
            const decodedPayload = atob(payload);
            const parsedData = JSON.parse(decodedPayload);
            return parsedData.userId || parsedData.sub || parsedData.id;
        } catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    };

    // Helper function to extract domain from URL
    const getDomainFromUrl = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace('www.', '');
        } catch {
            // Fallback for invalid URLs
            const match = url.match(/https?:\/\/([^\/]+)/);
            return match ? match[1].replace('www.', '') : 'Unknown';
        }
    };
    const [newRule, setNewRule] = useState({
        name: "",
        enabled: true,
        cooldownMinutes: 5,
        conditions: [
            {
                field: "eventname",
                operator: "contains",
                value: ""
            }
        ]
    });

    // Fetch rules on component mount
    useEffect(() => {
        fetchMonitoringStatus();
        fetchRules();
        fetchLiveFeed();
        fetchUserFeed();
        
        // Set up live feed polling every 5 seconds
        const interval = setInterval(() => {
            fetchLiveFeed();
            fetchUserFeed();
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchMonitoringStatus = async () => {
        try {
            const response = await fetch('https://api.cartlybot.io/api/monitor/status', {
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setMonitoringEnabled(data.enabled);
            }
        } catch (error) {
            console.error("Error fetching monitoring status:", error);
        }
    };

    const fetchRules = async () => {
        try {
            const response = await fetch('https://api.cartlybot.io/api/monitor/rules', {
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setRules(data);
            }
        } catch (error) {
            console.error("Error fetching rules:", error);
        }
    };

    const fetchLiveFeed = async () => {
        try {
            const response = await fetch('https://api.cartlybot.io/api/monitor/feed');
            
            if (response.ok) {
                const data = await response.json();
                setLiveFeed(data);
            }
        } catch (error) {
            console.error("Error fetching live feed:", error);
        }
    };

    const fetchUserFeed = async () => {
        try {
            const userId = getUserIdFromToken();
            if (!userId) return;

            const response = await fetch(`https://api.cartlybot.io/api/monitor/userFeed/${userId}`, {
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setUserFeed(data);
            }
        } catch (error) {
            console.error("Error fetching user feed:", error);
        }
    };

    const toggleMonitoring = async () => {
        try {
            const response = await fetch('https://api.cartlybot.io/api/monitor/toggle', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getAuthToken()}`
                },
                body: JSON.stringify({ enabled: !monitoringEnabled })
            });
            
            if (response.ok) {
                setMonitoringEnabled(!monitoringEnabled);
            }
        } catch (error) {
            console.error("Error toggling monitoring:", error);
        }
    };

    const toggleRuleEnabled = async (ruleId) => {
        try {
            const response = await fetch(`https://api.cartlybot.io/api/monitor/rules/${ruleId}/toggle`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                }
            });
            
            if (response.ok) {
                setRules(rules.map(rule => 
                    rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
                ));
            }
        } catch (error) {
            console.error("Error toggling rule:", error);
        }
    };

    const deleteRule = async (ruleId) => {
        try {
            const response = await fetch(`https://api.cartlybot.io/api/monitor/rules/${ruleId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                }
            });
            
            if (response.ok) {
                setRules(rules.filter(rule => rule.id !== ruleId));
            }
        } catch (error) {
            console.error("Error deleting rule:", error);
        }
    };

    const addCondition = () => {
        setNewRule({
            ...newRule,
            conditions: [
                ...newRule.conditions,
                { field: "eventname", operator: "contains", value: "" }
            ]
        });
    };

    const removeCondition = (index) => {
        setNewRule({
            ...newRule,
            conditions: newRule.conditions.filter((_, i) => i !== index)
        });
    };

    const updateCondition = (index, field, value) => {
        const updatedConditions = [...newRule.conditions];
        updatedConditions[index][field] = value;
        setNewRule({ ...newRule, conditions: updatedConditions });
    };

    const saveRule = async () => {
        try {
            const response = await fetch('https://api.cartlybot.io/api/monitor/rules', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getAuthToken()}`
                },
                body: JSON.stringify(newRule)
            });
            
            if (response.ok) {
                const savedRule = await response.json();
                setRules([...rules, savedRule]);
                setNewRule({
                    name: "",
                    enabled: true,
                    cooldownMinutes: 5,
                    conditions: [{ field: "eventname", operator: "contains", value: "" }]
                });
                setShowCreateRule(false);
            }
        } catch (error) {
            console.error("Error saving rule:", error);
        }
    };

    const fieldOptions = [
        { value: "eventname", label: "Event Name" },
        { value: "stock", label: "Stock" },
        { value: "eventurl", label: "Event URL" },
        { value: "sections", label: "Sections" }
    ];

    const operatorOptions = [
        { value: "contains", label: "Contains" },
        { value: "not_contains", label: "Does Not Contain" },
        { value: "equals", label: "Equals" },
        { value: "not_equals", label: "Does Not Equal" },
        { value: "greater_than", label: "Greater Than" },
        { value: "less_than", label: "Less Than" },
        { value: "greater_equal", label: "Greater Than or Equal" },
        { value: "less_equal", label: "Less Than or Equal" }
    ];

    return (
        <div className="flex flex-row">
            <Nav />
            <div className="h-screen w-screen p-5 text-white pl-10 pr-10">
                <header className="flex justify-between items-center mb-5">
                    <h1 className="text-sm font-medium flex flex-row gap-2 items-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="#7CF3A9"/>
                        </svg>
                        Monitor
                    </h1>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm">Monitoring:</span>
                            <button
                                onClick={toggleMonitoring}
                                className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                                    monitoringEnabled 
                                        ? 'bg-[#7CF3A9] text-black' 
                                        : 'bg-[#FF3F3F] text-white'
                                }`}
                            >
                                {monitoringEnabled ? 'Enabled' : 'Disabled'}
                            </button>
                        </div>
                    </div>
                </header>

                <div className="grid grid-cols-2 gap-6 h-[calc(100vh-120px)]">
                    {/* Rules Section */}
                    <div className="bg-input p-6 rounded">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Rules</h2>
                            <button
                                onClick={() => setShowCreateRule(true)}
                                className="px-4 py-2 rounded text-white font-semibold hover:opacity-80 transition-opacity"
                                style={{
                                    background: "linear-gradient(180deg, #63D78F 0%, #26C6B4 50%, #00BBCC 100%)",
                                }}
                            >
                                Create Rule
                            </button>
                        </div>

                        <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-250px)]">
                            {rules.map((rule) => (
                                <div key={rule.id} className="bg-[#0e1119] p-4 rounded border border-[#495064]">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-medium text-white">{rule.name}</h3>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => toggleRuleEnabled(rule.id)}
                                                className={`px-3 py-1 rounded text-xs ${
                                                    rule.enabled 
                                                        ? 'bg-[#7CF3A9] text-black' 
                                                        : 'bg-[#495064] text-white'
                                                }`}
                                            >
                                                {rule.enabled ? 'Enabled' : 'Disabled'}
                                            </button>
                                            <button
                                                onClick={() => deleteRule(rule.id)}
                                                className="px-3 py-1 rounded text-xs bg-[#FF3F3F] text-white hover:opacity-80"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-sm text-[#495064] mb-2">
                                        Cooldown: {rule.cooldownMinutes} minutes
                                    </div>
                                    <div className="space-y-1">
                                        {rule.conditions.map((condition, index) => (
                                            <div key={index} className="text-xs text-[#7CF3A9]">
                                                {condition.field} {condition.operator.replace('_', ' ')} "{condition.value}"
                                                {index < rule.conditions.length - 1 && <span className="text-white"> AND </span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Live Feed Section */}
                    <div className="bg-input p-6 rounded">
                        <div className="flex items-center gap-2 mb-4">
                            <h2 className="text-lg font-semibold">Live Feed</h2>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-[#FF3F3F] rounded-full animate-pulse"></div>
                                <span className="text-sm text-[#7CF3A9]">LIVE</span>
                            </div>
                        </div>
                        
                        <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-250px)]">
                            {userFeed.length === 0 ? (
                                <div className="text-center text-[#495064] py-8">
                                    <p>No events yet. Monitoring is active.</p>
                                </div>
                            ) : (
                                userFeed.map((item) => (
                                <div key={item.id}>
                                    {item.ruleTriggered ? (
                                        // Discord embed style for rule triggers
                                        <div className="bg-[#0e1119] rounded border-l-4 border-[#7CF3A9] overflow-hidden">
                                            <div className="p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-2 h-2 bg-[#7CF3A9] rounded-full"></div>
                                                    <span className="text-xs font-medium text-[#7CF3A9]">
                                                        Rule Triggered: {item.ruleName}
                                                    </span>
                                                    <span className="text-xs text-[#495064] ml-auto">
                                                        {new Date(item.timestamp).toLocaleString()}
                                                    </span>
                                                </div>
                                                
                                                <div className="flex gap-3">
                                                    {item.image && (
                                                        <img 
                                                            src={item.image} 
                                                            alt="Event" 
                                                            className="w-20 h-20 rounded object-cover flex-shrink-0"
                                                        />
                                                    )}
                                                    
                                                    <div className="flex-1 min-w-0">
                                                        <div className="mb-2">
                                                            <button
                                                                onClick={() => item.eventUrl && window.open(item.eventUrl, '_blank')}
                                                                className="text-white font-medium hover:text-[#7CF3A9] transition-colors cursor-pointer text-left"
                                                            >
                                                                {item.eventName}
                                                            </button>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            {item.eventDate && (
                                                                <div>
                                                                    <span className="text-[#495064]">Date:</span>
                                                                    <span className="text-white ml-1">{item.eventDate}</span>
                                                                </div>
                                                            )}
                                                            
                                                            {item.eventUrl && (
                                                                <div>
                                                                    <span className="text-[#495064]">Site:</span>
                                                                    <span className="text-white ml-1">
                                                                        {getDomainFromUrl(item.eventUrl)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {item.stock !== undefined && item.stock !== null && (
                                                                <div>
                                                                    <span className="text-[#495064]">Stock:</span>
                                                                    <span className={`ml-1 font-medium ${
                                                                        item.stock === 0 ? 'text-[#495064]' :
                                                                        item.stock > 50 ? 'text-[#7CF3A9]' : 
                                                                        item.stock > 10 ? 'text-[#FFC44E]' : 'text-[#FF3F3F]'
                                                                    }`}>
                                                                        {item.stock === 0 ? 'N/A' : item.stock}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {item.sections && item.sections.length > 0 && (
                                                                <div className="col-span-2">
                                                                    <span className="text-[#495064]">Sections:</span>
                                                                    <span className="text-white ml-1">
                                                                        {item.sections.join(", ")}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Discord embed style for non-triggered events (gray border)
                                        <div className="bg-[#0e1119] rounded border-l-4 border-[#495064] overflow-hidden">
                                            <div className="p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-2 h-2 bg-[#495064] rounded-full"></div>
                                                    <span className="text-xs font-medium text-[#495064]">
                                                        Event Update
                                                    </span>
                                                    <span className="text-xs text-[#495064] ml-auto">
                                                        {new Date(item.timestamp).toLocaleString()}
                                                    </span>
                                                </div>
                                                
                                                <div className="flex gap-3">
                                                    {item.image && (
                                                        <img 
                                                            src={item.image} 
                                                            alt="Event" 
                                                            className="w-20 h-20 rounded object-cover flex-shrink-0"
                                                        />
                                                    )}
                                                    
                                                    <div className="flex-1 min-w-0">
                                                        <div className="mb-2">
                                                            <button
                                                                onClick={() => item.eventUrl && window.open(item.eventUrl, '_blank')}
                                                                className="text-white font-medium hover:text-[#7CF3A9] transition-colors cursor-pointer text-left"
                                                            >
                                                                {item.eventName}
                                                            </button>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            {item.eventDate && (
                                                                <div>
                                                                    <span className="text-[#495064]">Date:</span>
                                                                    <span className="text-white ml-1">{item.eventDate}</span>
                                                                </div>
                                                            )}
                                                            
                                                            {item.eventUrl && (
                                                                <div>
                                                                    <span className="text-[#495064]">Site:</span>
                                                                    <span className="text-white ml-1">
                                                                        {getDomainFromUrl(item.eventUrl)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {item.stock !== undefined && item.stock !== null && (
                                                                <div>
                                                                    <span className="text-[#495064]">Stock:</span>
                                                                    <span className={`ml-1 font-medium ${
                                                                        item.stock === 0 ? 'text-[#495064]' :
                                                                        item.stock > 50 ? 'text-[#7CF3A9]' : 
                                                                        item.stock > 10 ? 'text-[#FFC44E]' : 'text-[#FF3F3F]'
                                                                    }`}>
                                                                        {item.stock === 0 ? 'N/A' : item.stock}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {item.sections && item.sections.length > 0 && (
                                                                <div className="col-span-2">
                                                                    <span className="text-[#495064]">Sections:</span>
                                                                    <span className="text-white ml-1">
                                                                        {item.sections.join(", ")}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Create Rule Modal */}
                {showCreateRule && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-input p-6 rounded-lg w-[600px] max-h-[80vh] overflow-y-auto">
                            <h2 className="text-lg font-semibold mb-4">Create New Rule</h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Rule Name</label>
                                    <input
                                        type="text"
                                        value={newRule.name}
                                        onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                                        className="w-full bg-[#0e1119] border border-[#495064] rounded px-3 py-2 text-white"
                                        placeholder="Enter rule name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Cooldown (minutes)</label>
                                    <input
                                        type="number"
                                        value={newRule.cooldownMinutes}
                                        onChange={(e) => setNewRule({ ...newRule, cooldownMinutes: parseInt(e.target.value) })}
                                        className="w-full bg-[#0e1119] border border-[#495064] rounded px-3 py-2 text-white"
                                        min="1"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium">Conditions</label>
                                        <button
                                            onClick={addCondition}
                                            className="px-3 py-1 bg-[#7CF3A9] text-black rounded text-sm hover:opacity-80"
                                        >
                                            Add Condition
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {newRule.conditions.map((condition, index) => (
                                            <div key={index} className="flex gap-2 items-center">
                                                <select
                                                    value={condition.field}
                                                    onChange={(e) => updateCondition(index, 'field', e.target.value)}
                                                    className="bg-[#0e1119] border border-[#495064] rounded px-3 py-2 text-white"
                                                >
                                                    {fieldOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                
                                                <select
                                                    value={condition.operator}
                                                    onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                                                    className="bg-[#0e1119] border border-[#495064] rounded px-3 py-2 text-white"
                                                >
                                                    {operatorOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                
                                                <input
                                                    type="text"
                                                    value={condition.value}
                                                    onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                    className="flex-1 bg-[#0e1119] border border-[#495064] rounded px-3 py-2 text-white"
                                                    placeholder="Value"
                                                />
                                                
                                                {newRule.conditions.length > 1 && (
                                                    <button
                                                        onClick={() => removeCondition(index)}
                                                        className="px-2 py-2 bg-[#FF3F3F] text-white rounded hover:opacity-80"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowCreateRule(false)}
                                    className="px-4 py-2 bg-[#495064] text-white rounded hover:opacity-80"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveRule}
                                    className="px-4 py-2 rounded text-white font-semibold hover:opacity-80"
                                    style={{
                                        background: "linear-gradient(180deg, #63D78F 0%, #26C6B4 50%, #00BBCC 100%)",
                                    }}
                                >
                                    Save Rule
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Monitor;
